import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { toast } from 'react-toastify';
import { useChat } from '../../common/ChatContext';
import { formatMessageContent } from './formatMessageContent';
import { formatDateTime } from '../../common/utils';

const ACCENT = '#137274';
// Must match backend/src/services/chatService.js's EDIT_WINDOW_MS — this
// copy only decides whether to show the "Edit" option; the backend is the
// actual enforcement, so a stale/mismatched value here is a UX nit, not a
// security hole.
const EDIT_WINDOW_MS = 12 * 60 * 60 * 1000;

const ChatThread = ({ conversation }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const {
    currentUserId,
    messagesByConversation,
    typingByConversation,
    sendMessage,
    editMessage,
    deleteMessage,
    notifyTyping,
    notifyTypingStop,
  } = useChat();

  const messages = messagesByConversation[String(conversation.id)] || [];
  const partnerId = conversation.partner?.id;
  const isPartnerTyping = String(typingByConversation[String(conversation.id)] || '') === String(partnerId);

  const [draft, setDraft] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuMessage, setMenuMessage] = useState(null);
  // Set while editing an existing message — the main composer below is
  // reused as the editor (pre-filled with the message's text) rather than
  // editing inline in the bubble, so there's one place to type and one
  // Enter/Send action that does the right thing depending on this.
  const [editingMessage, setEditingMessage] = useState(null);
  // Timestamps stay hidden until the user taps a bubble to reveal them —
  // keeps the thread from being cluttered with a time under every line.
  const [revealedTimestampId, setRevealedTimestampId] = useState(null);
  const bottomRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const composerInputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, conversation.id]);

  useEffect(() => () => clearTimeout(typingTimeoutRef.current), []);

  // ChatThread isn't remounted when the user switches to a different
  // conversation (no per-conversation `key` upstream), so an in-progress
  // edit has to be dropped explicitly here — otherwise sending in the
  // newly-opened conversation would silently apply as an edit to a message
  // in the one just left.
  useEffect(() => {
    setEditingMessage(null);
  }, [conversation.id]);

  // Opening a conversation (first load or switching between chats) should
  // drop the user straight into typing, like any chat app.
  useEffect(() => {
    composerInputRef.current?.focus();
  }, [conversation.id]);

  const handleDraftChange = (e) => {
    setDraft(e.target.value);
    notifyTyping(conversation.id, partnerId);
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => notifyTypingStop(conversation.id, partnerId), 2000);
  };

  const cancelEdit = () => {
    setEditingMessage(null);
    setDraft('');
  };

  const handleSend = async () => {
    const text = draft.trim();
    if (!text) return;

    if (editingMessage) {
      const id = editingMessage.id;
      setEditingMessage(null);
      setDraft('');
      try {
        await editMessage(id, text);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Could not save the edit.');
      }
      return;
    }

    setDraft('');
    clearTimeout(typingTimeoutRef.current);
    notifyTypingStop(conversation.id, partnerId);
    await sendMessage(conversation.id, text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === 'Escape' && editingMessage) {
      cancelEdit();
    }
  };

  const openMenu = (e, message) => {
    setMenuAnchor(e.currentTarget);
    setMenuMessage(message);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuMessage(null);
  };

  const startEdit = () => {
    const message = menuMessage;
    closeMenu();
    setEditingMessage(message);
    setDraft(message.text);
    // Menu's `disableRestoreFocus` stops it from yanking focus back onto
    // the icon that opened it; this grabs it for the composer explicitly.
    composerInputRef.current?.focus();
  };

  const handleDelete = async () => {
    const id = menuMessage.id;
    closeMenu();
    try {
      await deleteMessage(id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete the message.');
    }
  };

  const bubbles = messages.map((message) => {
        const isMine = String(message.sender) === String(currentUserId);
        const isDeleted = Boolean(message.deletedAt);
        const isBeingEdited = editingMessage?.id === message.id;
        // A fenced code block already renders its own dark card — wrapping
        // that in the teal "mine" bubble on top just clashes, so messages
        // with code drop the colored bubble entirely.
        const hasCodeBlock = !isDeleted && /```/.test(message.text || '');
        return (
          <Box
            key={message.id}
            sx={{
              display: 'flex',
              justifyContent: isMine ? 'flex-end' : 'flex-start',
              px: 2,
              py: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: '78%' }}>
              {isMine && !isDeleted && (
                <IconButton size="small" onClick={(e) => openMenu(e, message)} sx={{ order: 2, p: 0.25 }}>
                  <MoreVertRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
              <Box
                onClick={() =>
                  setRevealedTimestampId((id) => (id === message.id ? null : message.id))
                }
                sx={{
                  order: 1,
                  bgcolor: hasCodeBlock
                    ? 'transparent'
                    : isMine
                    ? ACCENT
                    : isDark
                    ? alpha('#fff', 0.08)
                    : alpha('#000', 0.06),
                  color: hasCodeBlock ? 'text.primary' : isMine ? '#fff' : 'text.primary',
                  borderRadius: 2.5,
                  px: 1.5,
                  py: 1,
                  cursor: 'pointer',
                  outline: isBeingEdited ? `2px solid ${ACCENT}` : 'none',
                  outlineOffset: 2,
                }}
              >
                <Typography
                  component="div"
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontStyle: isDeleted ? 'italic' : 'normal', opacity: isDeleted ? 0.7 : 1 }}
                >
                  {isDeleted ? 'Message deleted' : formatMessageContent(message.text)}
                </Typography>
                {revealedTimestampId === message.id && (
                  <Typography variant="caption" sx={{ display: 'block', opacity: 0.75, mt: 0.25 }}>
                    {formatDateTime(message.createdAt).date} · {formatDateTime(message.createdAt).time}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end', mt: 0.25 }}>
                  {message.editedAt && !isDeleted && (
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      edited
                    </Typography>
                  )}
                  {isMine &&
                    (message.status === 'read' ? (
                      <DoneAllRoundedIcon sx={{ fontSize: 14, opacity: 1 }} titleAccess="Read" />
                    ) : message.status === 'delivered' ? (
                      <DoneAllRoundedIcon sx={{ fontSize: 14, opacity: 0.6 }} titleAccess="Delivered" />
                    ) : (
                      <DoneRoundedIcon sx={{ fontSize: 14, opacity: 0.6 }} titleAccess="Sent" />
                    ))}
                </Box>
              </Box>
            </Box>
          </Box>
        );
      });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 2, pb: 1 }}>
          <Tooltip title="Messages are protected with industry-standard encryption before being stored.">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.5,
                borderRadius: 5,
                bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#000', 0.04),
                color: 'text.secondary',
                cursor: 'default',
              }}
            >
              <LockRoundedIcon sx={{ fontSize: 13 }} />
              <Typography variant="caption">Messages are encrypted</Typography>
            </Box>
          </Tooltip>
        </Box>
        {bubbles}
        {isPartnerTyping && (
          <Typography variant="caption" color="text.secondary" sx={{ px: 2.5 }}>
            {conversation.partner?.name || 'They'} is typing…
          </Typography>
        )}
        <div ref={bottomRef} />
      </Box>
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: isDark ? alpha('#fff', 0.08) : alpha('#000', 0.08),
        }}
      >
        {editingMessage && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              pt: 1,
              color: ACCENT,
            }}
          >
            <EditRoundedIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" sx={{ flex: 1 }}>
              Editing message
            </Typography>
            <IconButton size="small" onClick={cancelEdit} sx={{ p: 0.25, color: 'inherit' }}>
              <CloseRoundedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1, p: 1.5 }}>
          <TextField
            inputRef={composerInputRef}
            fullWidth
            size="small"
            placeholder="Write a message… (```code``` for a code block)"
            value={draft}
            onChange={handleDraftChange}
            onKeyDown={handleKeyDown}
            multiline
            maxRows={8}
          />
          <IconButton onClick={handleSend} disabled={!draft.trim()} sx={{ color: ACCENT }}>
            <SendRoundedIcon />
          </IconButton>
        </Box>
      </Box>

      {/* disableRestoreFocus: MUI's Menu, by default, forces focus back onto
          the icon that opened it once its close transition finishes — which
          would otherwise yank focus away from the composer right after
          "Edit" moves it there. */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        disableRestoreFocus
      >
        {menuMessage && Date.now() - new Date(menuMessage.createdAt).getTime() <= EDIT_WINDOW_MS && (
          <MenuItem onClick={startEdit}>Edit</MenuItem>
        )}
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ChatThread;
